import React, {
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

export interface ConfirmLeaveContextProps {
    count: number;
    increaseCount(): void;
    decreaseCount(): void;
}
const noop = () => undefined;

const context = React.createContext<ConfirmLeaveContextProps>({
    count: 0,
    increaseCount: noop,
    decreaseCount: noop,
});

interface ProviderProps {
    children: ReactNode;
}

export function ConfirmLeaveProvider({ children }: ProviderProps) {
    const { t } = useTranslation();
    const [count, setCount] = useState(0);
    const increaseCount = useCallback(() => setCount((c) => c + 1), []);
    const decreaseCount = useCallback(() => setCount((c) => c - 1), []);

    const location = useLocation();
    const previousLocationRef = useRef(location.pathname);

    useEffect(() => {
        if (count > 0) {
            window.onbeforeunload = () =>
                t('client.editableDetails.discardChangesConfirmationMessage');
        } else {
            window.onbeforeunload = null;
        }
    }, [t, count]);

    // Handle navigation changes when there are unsaved changes
    useEffect(() => {
        if (count > 0 && location.pathname !== previousLocationRef.current) {
            // Navigation has occurred while there are unsaved changes
            // Note: This is a simplified version - in a data router we'd use useBlocker
            // For now, we'll rely on the beforeunload handler for browser navigation
            // and the component-level confirmation for programmatic navigation
        }
        previousLocationRef.current = location.pathname;
    }, [location.pathname, count]);

    // useMemo required - context value passed to provider.
    // React Compiler doesn't optimize context values automatically.
    const contextValue = useMemo(
        () => ({ count, increaseCount, decreaseCount }),
        [count, increaseCount, decreaseCount],
    );

    return <context.Provider value={contextValue}>{children}</context.Provider>;
}

export const useConfirmLeave = (editing: boolean) => {
    const { increaseCount, decreaseCount } = useContext(context);
    const countedRef = useRef(false);
    useEffect(() => {
        if (editing && !countedRef.current) {
            increaseCount();
            countedRef.current = true;
        } else if (!editing && countedRef.current) {
            decreaseCount();
            countedRef.current = false;
        }
    }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps
    // Make sure count is unset on unmount
    useEffect(
        () => () => {
            if (countedRef.current) {
                decreaseCount();
            }
        },
        [], // eslint-disable-line react-hooks/exhaustive-deps
    );
};
